import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';

const API = appConfig.apis.personApiEnv;
const GEO = appConfig.apis.geolocationApiEnv;

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

export interface PersonCityState {
    city: string;
    state: string;
}

export interface PersonFullAddress {
    street: string;
    number: string;
    complement: string;
    district: string;
    city: string;
    state: string;
    zip_code: string;
    ibge_code: string;
}

export interface IndividualPerson {
    uuid: string;
    name: string;
    socialId: string;
    email: string;
    mobile: string;
}

export interface LegalPerson {
    personId: string;
    legalName: string;
    govTaxId: string;
}

export interface Signatory {
    individualUuid: string;
    fullName: string;
    socialId: string;
    email: string;
    mobile: string;
    position: string;
    isActive: boolean;
    allowedOperations: Array<{ operation: string; representationFormat: string } | string>;
    signAsEntries: { signAs: string; useDigitalCertificate: boolean }[];
    proxyExpirationDate?: string | null;
    proxyAllowedValue?: number | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    lastVerifiedAt?: string | null;
}

export interface LegalPersonSearchResult {
    personId: string;
    legalName: string;
    fantasyName: string;
    govTaxId: string;
    govTaxIdFormatted: string;
}

export interface PersonByDocument {
    uuid: string;          // personId
    name: string;
    personType: 'INDIVIDUAL' | 'LEGAL_PERSON';
    document: string | null;
}

export interface PersonById {
    personId: string;
    name: string;
    personType: 'INDIVIDUAL' | 'LEGAL_PERSON';
    document: string | null;
}

export const personApi = {
    searchLegalPersons: async (query: string, signal?: AbortSignal): Promise<LegalPersonSearchResult[]> => {
        const stripped = query.replace(/[.\-/]/g, '').toUpperCase();
        const hasConsecutiveDigits = /\d{2,}/.test(stripped);
        const hasNoSpaces = !/\s/.test(stripped);
        const isCnpjSearch = hasConsecutiveDigits && hasNoSpaces && stripped.length <= 14;
        const queryParam = isCnpjSearch
            ? `govTaxId=${encodeURIComponent(stripped)}`
            : `legalName=${encodeURIComponent(query)}`;
        const res = await fetch(
            `${API.address}/legal-persons/page-list?${queryParam}&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders(), signal }
        );
        if (!res.ok) {
            const errData = await res.json().catch(() => null);
            const detail = errData?.message || `status ${res.status}`;
            throw new Error(`Erro ao buscar cedente: ${detail}`);
        }
        const data = await res.json();
        return (data.list || []).slice(0, 6);
    },

    /**
     * Search people by name, email or CPF.
     * - Query contains '@' → searches by email parameter
     * - Otherwise → searches by name parameter (Person API matches name OR socialId)
     * Returns all matches (including those without email).
     */
    searchPeople: async (query: string, signal?: AbortSignal): Promise<IndividualPerson[]> => {
        const clean = query.trim();
        if (!clean) return [];

        let param: string;
        if (clean.includes('@')) {
            param = `email=${encodeURIComponent(clean)}&name=&mobile=`;
        } else {
            const stripped = clean.replace(/\D/g, '');
            const isNumeric = /^\d+$/.test(stripped) && stripped.length >= 3;
            const nameValue = isNumeric ? stripped : clean;
            param = `name=${encodeURIComponent(nameValue)}&email=&mobile=`;
        }

        const res = await fetch(
            `${API.address}/individual/page-list?${param}&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders(), signal }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.list || [];
    },

    searchByNameOrCpf: async (query: string): Promise<IndividualPerson[]> => {
        const clean = query.trim();
        if (!clean) return [];
        const isNumeric = /^\d+$/.test(clean.replace(/\D/g, '')) && clean.replace(/\D/g, '').length >= 3;
        const param = isNumeric ? `name=${clean.replace(/\D/g, '')}` : `name=${encodeURIComponent(clean)}`;
        const res = await fetch(
            `${API.address}/individual/page-list?${param}&email=&mobile=&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return (data.list || []).filter((p: any) => p.email);
    },

    searchIndividualByCpf: async (cpf: string): Promise<IndividualPerson | null> => {
        const clean = cpf.replace(/\D/g, '');
        if (clean.length !== 11) return null;
        const res = await fetch(
            `${API.address}/individual/page-list?name=${clean}&email=&mobile=&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const list: any[] = data.list || [];
        return list.find((p: any) => p.socialId?.replace(/\D/g, '') === clean) || null;
    },

    searchLegalPersonByCnpj: async (cnpj: string): Promise<LegalPerson | null> => {
        const clean = cnpj.replace(/\D/g, '');
        if (clean.length !== 14) return null;
        const res = await fetch(
            `${API.address}/legal-persons/page-list?govTaxId=${encodeURIComponent(clean)}&legalName=&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const list: any[] = data.list || [];
        return list.find((p: any) => p.govTaxId?.replace(/\D/g, '') === clean) || null;
    },

    getSignatories: async (legalPersonUuid: string): Promise<Signatory[]> => {
        const res = await fetch(
            `${API.address}/legal-persons/${legalPersonUuid}/signatories`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.signatories || [];
    },

    /**
    /**
     * Resolves the first registered address of a person into city + state.
     * Falls back to the geolocation service when the address only carries a cityId.
     */
    getPersonAddress: async (personId: string): Promise<PersonCityState | null> => {
        if (!personId || personId === '00000000-0000-0000-0000-000000000000') return null;
        const res = await fetch(
            `${API.address}/person/${personId}/addresses`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const addresses = data.addresses || data || [];
        if (!Array.isArray(addresses) || addresses.length === 0) return null;
        const addr = addresses[0];

        let city = addr.city || '';
        let state = addr.state || addr.province || '';

        if (addr.cityId && (!city || !state)) {
            try {
                const cityRes = await fetch(
                    `${GEO.address}/cities/${addr.cityId}?fields=id,name,province`,
                    { headers: { 'x-api-key': GEO.key } }
                );
                if (cityRes.ok) {
                    const cityData = await cityRes.json();
                    if (!city) city = cityData.name || '';
                    if (!state) state = cityData.province?.initials || cityData.province?.abbreviation || cityData.province || '';
                }
            } catch { /* ignore geolocation errors, return what we have */ }
        }

        return { city, state };
    },

    /**
     * Resolves the first registered address of a person into a full address
     * (street, number, complement, district, city, state, zip, ibge).
     * Falls back to the geolocation service when the address only carries a cityId.
     */
    getPersonFullAddress: async (personId: string): Promise<PersonFullAddress | null> => {
        if (!personId || personId === '00000000-0000-0000-0000-000000000000') return null;
        const res = await fetch(
            `${API.address}/person/${personId}/addresses`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const addresses = data.addresses || data || [];
        if (!Array.isArray(addresses) || addresses.length === 0) return null;
        const addr = addresses[0];

        let city = addr.city || '';
        let state = addr.state || addr.province || '';
        let ibgeCode = addr.ibgeCode || addr.ibge_code || '';

        if (addr.cityId && (!city || !state || !ibgeCode)) {
            try {
                const cityRes = await fetch(
                    `${GEO.address}/cities/${addr.cityId}?fields=id,name,ibge,province`,
                    { headers: { 'x-api-key': GEO.key } }
                );
                if (cityRes.ok) {
                    const cityData = await cityRes.json();
                    if (!city) city = cityData.name || '';
                    if (!state) state = cityData.province?.initials || cityData.province?.abbreviation || cityData.province || '';
                    if (!ibgeCode) ibgeCode = cityData.ibge || cityData.ibgeCode || '';
                }
            } catch { /* ignore geolocation errors, return what we have */ }
        }

        return {
            street: addr.street || '',
            number: addr.number || '',
            complement: addr.complement || '',
            district: addr.district || addr.neighborhood || '',
            city,
            state,
            zip_code: addr.zipcode || addr.zip_code || '',
            ibge_code: ibgeCode,
        };
    },

    /**
     * Resolve uma pessoa (PF ou PJ) a partir do documento (CPF/CNPJ).
     * Usa GET /person/all/<doc>, que normaliza o documento no backend (com ou sem
     * máscara) e busca por igualdade de CPF/CNPJ. Só retorna a pessoa cujo documento
     * **casa exatamente** o digitado (normalizado); caso contrário, null. Sem fallback
     * para "primeiro da lista": vincular a pessoa errada a um caso jurídico seria um erro
     * grave e silencioso — melhor devolver null e a UI oferecer contraparte externa.
     * O `uuid` do resultado é o personId.
     */
    findPersonByDocument: async (
        document: string,
        signal?: AbortSignal,
    ): Promise<PersonByDocument | null> => {
        const clean = document.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (clean.length !== 11 && clean.length !== 14) return null;

        const res = await fetch(
            `${API.address}/person/all/${encodeURIComponent(clean)}`,
            { headers: await getHeaders(), signal },
        );
        if (!res.ok) return null;

        const data = await res.json();
        const results: any[] = data.results || [];
        const matchesDocument = (r: any) =>
            (r.documents || []).some(
                (d: any) => (d.number || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase() === clean,
            );

        const match = results.find(matchesDocument);
        if (!match) return null;

        const doc = (match.documents || []).find(
            (d: any) => (d.number || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase() === clean,
        );
        return {
            uuid: match.uuid,
            name: match.name,
            personType: match.personType,
            document: doc?.number || null,
        };
    },

    /**
     * Resolve nome + documento a partir do personId (UUID), sem saber se é PF ou PJ.
     * Não há endpoint único no `person`: tenta o individual (PF); em 404, cai para o
     * legal-person (PJ). Retorna null se não achar em nenhum dos dois. Usado para exibir
     * nome/documento na lista de partes sem copiar o dado no recovery (ADR 001).
     */
    resolvePersonById: async (
        personId: string,
        signal?: AbortSignal,
    ): Promise<PersonById | null> => {
        const headers = await getHeaders();

        // /individual/<id>/page-form é o endpoint batido para PF por UUID (usado por
        // IndividualPageEdit, IndividualFormConsentingParty etc.). Evita a rota "nua"
        // /individual/<id>, que colide com /individual/<socialId> no resolver do person.
        const individual = await fetch(
            `${API.address}/individual/${personId}/page-form`,
            { headers, signal },
        );
        if (individual.ok) {
            const data = await individual.json();
            return {
                personId,
                name: data.name || '',
                personType: 'INDIVIDUAL',
                document: data.socialId || null,
            };
        }

        const legal = await fetch(
            `${API.address}/legal-persons/${personId}/page-form`,
            { headers, signal },
        );
        if (legal.ok) {
            const data = await legal.json();
            return {
                personId,
                name: data.legalName || data.fantasyName || '',
                personType: 'LEGAL_PERSON',
                document: data.govTaxId || null,
            };
        }

        return null;
    },
};
